import { Navigate } from 'react-router-dom';
import { ReactNode, useEffect, useState } from 'react';

interface ProtectedRouteProps {
    children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
    const [isChecking, setIsChecking] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        // Check if user is authenticated
        const authToken = localStorage.getItem('authToken');
        const adminAuth = localStorage.getItem('adminAuth');
        
        // User is authenticated if they have both authToken and adminAuth flag
        const authenticated = Boolean(authToken && adminAuth === 'true');
        
        setIsAuthenticated(authenticated);
        setIsChecking(false);
    }, []);

    // Show nothing while checking authentication
    if (isChecking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
                <div className="animate-pulse text-primary">Loading...</div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Render protected content if authenticated
    return <>{children}</>;
}

// Super Admin Only Route - for pages like /admin/users
interface SuperAdminRouteProps {
    children: ReactNode;
}

export function SuperAdminRoute({ children }: SuperAdminRouteProps) {
    const [isChecking, setIsChecking] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    useEffect(() => {
        // Check if user is authenticated
        const authToken = localStorage.getItem('authToken');
        const adminAuth = localStorage.getItem('adminAuth');
        const adminUserStr = localStorage.getItem('adminUser');
        
        // User is authenticated if they have both authToken and adminAuth flag
        const authenticated = Boolean(authToken && adminAuth === 'true');
        
        // Check if user is super admin
        let superAdmin = false;
        if (authenticated && adminUserStr) {
            try {
                const adminUser = JSON.parse(adminUserStr);
                superAdmin = adminUser.role === 'super_admin';
            } catch (e) {
                console.error('Error parsing admin user:', e);
            }
        }
        
        setIsAuthenticated(authenticated);
        setIsSuperAdmin(superAdmin);
        setIsChecking(false);
    }, []);

    // Show nothing while checking authentication
    if (isChecking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
                <div className="animate-pulse text-primary">Loading...</div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Redirect to dashboard if not super admin
    if (!isSuperAdmin) {
        return <Navigate to="/admin" replace />;
    }

    // Render protected content if authenticated and super admin
    return <>{children}</>;
}
